import React, { Suspense, useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, NavLink, Link, useLocation } from "react-router-dom";
import {
  AppBar, Toolbar, Typography, Button, Container, Box, IconButton, Menu, MenuItem
} from "@mui/material";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./components/Theme";

import Home from "./components/Home";
import Footer from "./components/Footer";

const RankingsMen = React.lazy(() => import("./components/Rankings_Men"));
const RankingsWomen = React.lazy(() => import("./components/Rankings_Women"));
const Scoring = React.lazy(() => import("./components/Scoring"));
const AthleteDetail = React.lazy(() => import("./components/AthleteDetail"));
const About = React.lazy(() => import("./components/About"));
const AboutMe = React.lazy(() => import("./components/AboutMe"));
const Feedback = React.lazy(() => import("./components/Feedback"));
const PrivacyPolicy = React.lazy(() => import("./components/PrivacyPolicy"));

const apiUrl =
  process.env.REACT_APP_API_URL ||
  "https://my-cloud-run-service-296748539603.us-central1.run.app";

const navButtonSx = {
  color: "#071b33",
  fontWeight: 850,
  borderRadius: 2,
  px: 1.25,
  py: 0.7,
  mx: 0.3,
  backgroundColor: "#ffffff",
  border: "1px solid rgba(7, 27, 51, 0.12)",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#fbe9e7", color: "#b91f1a" },
  "&.active": { backgroundColor: "#c92520", color: "#ffffff", borderColor: "#c92520", boxShadow: "0 3px 8px rgba(201, 37, 32, 0.24)" },
};
const navItems = [
  ["Home", "/", true],
  ["How It Works", "/about"],
  ["Men's Rankings", "/rankings/men"],
  ["Women's Rankings", "/rankings/women"],
  ["Equivalent Performances", "/scoring"],
  ["Feedback", "/feedback"],
];
const siteUrl = "https://nytrax.run";
const routeSeo = {
  "/": {
    title: "NYTRAX | National Youth Track Athlete Index",
    description: "Explore national youth track and field rankings, athlete profiles, TRAX-Scores, and equivalent performance analytics.",
  },
  "/about": {
    title: "How NYTRAX Works | TRAX-Score and Event Scores",
    description: "Learn how NYTRAX uses AI-assisted analysis, machine learning, and track-specific analytics to evaluate youth track and field performances.",
  },
  "/about-us": {
    title: "About NYTRAX | Coleman Ferrell",
    description: "Meet NYTRAX founder Coleman Ferrell and learn how his background in collegiate running, statistics, and sports analytics shaped the platform.",
  },
  "/rankings/men": {
    title: "Men's High School Track and Field Rankings | NYTRAX",
    description: "Explore national and state rankings for men's high school track and field athletes, including TRAX-Scores and event performance context.",
  },
  "/rankings/women": {
    title: "Women's High School Track and Field Rankings | NYTRAX",
    description: "Explore national and state rankings for women's high school track and field athletes, including TRAX-Scores and event performance context.",
  },
  "/scoring": {
    title: "Equivalent Track and Field Performances | NYTRAX",
    description: "Calculate an Event Score and compare equivalent track and field performances across sprints, distance, hurdles, jumps, and throws.",
  },
  "/feedback": {
    title: "Feedback | NYTRAX",
    description: "Report a data correction, ask a ranking question, or share an idea with NYTRAX.",
  },
  "/privacy": {
    title: "Privacy Policy | NYTRAX",
    description: "Read the NYTRAX privacy policy and learn how submitted information is handled.",
  },
};

function setMetaContent(selector, content) {
  const element = document.querySelector(selector);
  if (element) element.setAttribute("content", content);
}

function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = pathname.startsWith("/athlete/")
      ? {
        title: "Athlete Profile | NYTRAX",
        description: "View this athlete's NYTRAX ranking, TRAX-Score, model-impact metrics, and complete performance history.",
      }
      : routeSeo[pathname] || routeSeo["/"];
    const canonicalUrl = `${siteUrl}${pathname === "/" ? "/" : pathname}`;

    document.title = seo.title;
    setMetaContent('meta[name="description"]', seo.description);
    setMetaContent('meta[property="og:title"]', seo.title);
    setMetaContent('meta[property="og:description"]', seo.description);
    setMetaContent('meta[property="og:url"]', canonicalUrl);
    setMetaContent('meta[name="twitter:title"]', seo.title);
    setMetaContent('meta[name="twitter:description"]', seo.description);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);
  }, [pathname]);

  return null;
}

function App() {
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const closeMobileMenu = () => setMobileMenuAnchor(null);

  return (
    <ThemeProvider theme={theme}>
      <Router>
            <SeoManager />
            <div className="App">
              <AppBar position="static" elevation={0} sx={{ backgroundColor: "#ffffff", color: "#071b33", borderBottom: "3px solid #c92520", boxShadow: "0 4px 16px rgba(7, 27, 51, 0.1)" }}>
                <Toolbar sx={{ minHeight: { xs: 64, lg: 74 }, px: { xs: 1.5, md: 3 } }}>
                  <Box
                    component={Link}
                    to="/"
                    aria-label="NYTRAX home"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      mr: "auto",
                      width: "fit-content",
                      color: "inherit",
                      textDecoration: "none",
                    }}
                  >
                    <Box component="img" src="/images/nytrax-logo.png" alt="NYTRAX logo" sx={{ width: { xs: 43, lg: 51 }, height: { xs: 43, lg: 51 }, objectFit: "contain", borderRadius: 1.5 }} />
                    <Typography variant="h6" sx={{ fontFamily: "'Arial Black', 'Trebuchet MS', sans-serif", fontStyle: "italic", fontSize: { xs: 21, lg: 24 }, fontWeight: 950, letterSpacing: -0.35, color: "#071b33" }}>
                      NYTRAX
                    </Typography>
                  </Box>
                  <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center" }}>
                    {navItems.map(([label, path, end]) => <Button key={path} component={NavLink} to={path} end={end} sx={navButtonSx}>{label}</Button>)}
                  </Box>
                  <IconButton aria-label="Open navigation menu" aria-controls={mobileMenuAnchor ? "nytrax-mobile-menu" : undefined} aria-expanded={Boolean(mobileMenuAnchor)} onClick={(event) => setMobileMenuAnchor(event.currentTarget)} sx={{ display: { xs: "inline-flex", lg: "none" }, color: "#071b33", border: "1px solid rgba(7,27,51,.15)", borderRadius: 1.5 }}>
                    <MenuRoundedIcon />
                  </IconButton>
                  <Menu id="nytrax-mobile-menu" anchorEl={mobileMenuAnchor} open={Boolean(mobileMenuAnchor)} onClose={closeMobileMenu} MenuListProps={{ "aria-label": "Site navigation" }} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                    {navItems.map(([label, path, end]) => <MenuItem key={path} component={NavLink} to={path} end={end} onClick={closeMobileMenu} sx={{ minWidth: 230, fontWeight: 800, "&.active": { color: "#c92520", backgroundColor: "#fbe9e7" } }}>{label}</MenuItem>)}
                  </Menu>
                </Toolbar>
              </AppBar>

              <Container maxWidth={false} sx={{ marginTop: 2, px: { xs: 2, md: 3 } }}>
                <Suspense fallback={<Typography>Loading...</Typography>}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/rankings/men" element={<RankingsMen apiUrl={apiUrl} />} /> {/* Men's Rankings */}
                    <Route path="/rankings/women" element={<RankingsWomen apiUrl={apiUrl} />} /> {/* Women's Rankings */}
                    <Route path="/scoring" element={<Scoring apiUrl={apiUrl} />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/about-us" element={<AboutMe />} />
                    <Route path="/feedback" element={<Feedback />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/athlete/:athleteId" element={<AthleteDetail />} />
                  </Routes>
                </Suspense>
              </Container>

              <Footer />
            </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
